import React, { useState, useEffect } from 'react';
import {
  CentralEdgeModem,
  ModemCapabilities,
  ModemModel,
  computeEffectiveCapabilities,
  normalizeCapabilities,
  getCapabilityValue,
  resolveProvisioningMethod,
  isSimPresent,
} from '@simcheck/shared-types';
import { DataTable, Column, Modal, Button, DeleteConfirmDialog, Label, Badge, Switch, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { StatusBadge } from './StatusBadge';
import { SignalStrengthBar } from './SignalStrengthBar';
import { ModemCapabilitiesEditor } from './ModemCapabilitiesEditor';
import { EuiccInventoryModal } from './EuiccInventoryModal';
import { ConnectivityModeSheet } from './ConnectivityModeSheet';
import { Smartphone, Settings, Wifi, Radio, Zap, Scan, Network, Link2, Unlink, ChevronDown, Info, Trash2, Cpu, RadioTower, Activity } from 'lucide-react';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import * as modemModelService from '@/services/modemModelService';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';

interface ModemsTabProps {
  /**
   * Unified list of modems — live, degraded, and offline — each carrying a
   * `status` field derived server-side from the `known_modems` subcollection.
   * Stale entries (not reporting within the freshness window) arrive as
   * `status === 'offline'` with their last-known data; gone-from-enumeration
   * modems are captured by the subcollection until an admin "forgets" them.
   */
  modems: CentralEdgeModem[];
  loading?: boolean;
  deviceId: string;
  /**
   * Device-level `skipEsimProvisioning`. Used to compute the "effective
   * method" badge next to each modem's method dropdown — when true, the
   * resolver short-circuits to `'skip'` regardless of per-modem setting.
   */
  deviceSkipEsimProvisioning?: boolean;
  /** #247: the device's backhaul APN — shown as the default in the uplink-switch dialog. */
  backhaulApn?: string | null;
  /** #252: owning org (connectivity modes are private-edge only; null = shared). */
  deviceOwnerOrgId?: string | null;
  onNetworksUpdated?: () => void;
  /** Notify parent after a forget so it can refetch. */
  onModemForgotten?: () => void;
}

export const ModemsTab: React.FC<ModemsTabProps> = ({ modems, loading, deviceId, deviceSkipEsimProvisioning, backhaulApn, deviceOwnerOrgId, onNetworksUpdated, onModemForgotten }) => {
  const { success, error: showError } = useToast();
  const fmt = useDateFormat();

  // Modem models from database (for dropdown)
  const [modemModels, setModemModels] = useState<ModemModel[]>([]);
  const [modemModelsLoading, setModemModelsLoading] = useState(true);

  const [capabilitiesModal, setCapabilitiesModal] = useState<{
    isOpen: boolean;
    modem: CentralEdgeModem | null;
    selectedModelId: string | null;      // Selected ModemModel from database
    selectedModel: ModemModel | null;    // Full ModemModel object
    customCapabilities: ModemCapabilities | undefined;  // Custom overrides
    showCustomCapabilities: boolean;     // Whether to show/edit custom capabilities
  }>({
    isOpen: false,
    modem: null,
    selectedModelId: null,
    selectedModel: null,
    customCapabilities: undefined,
    showCustomCapabilities: false,
  });

  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [scanningModems, setScanningModems] = useState<Set<string>>(new Set());
  const [priorityEdits, setPriorityEdits] = useState<Record<string, string>>({});
  const [savingPriority, setSavingPriority] = useState<Set<string>>(new Set());
  const [savingMethod, setSavingMethod] = useState<Set<string>>(new Set());
  const [savingCfunReset, setSavingCfunReset] = useState<Set<string>>(new Set());
  /** Optimistic cfunResetOnRelease state, keyed by modem IMEI. */
  const [cfunResetOptimistic, setCfunResetOptimistic] = useState<Record<string, boolean>>({});
  const [savingDisabled, setSavingDisabled] = useState<Set<string>>(new Set());
  /** Optimistic disabled state, keyed by modem IMEI. */
  const [disabledOptimistic, setDisabledOptimistic] = useState<Record<string, boolean>>({});
  /**
   * Optimistic eSIM method state, keyed by modem IMEI. Lets the dropdown
   * reflect a chosen value immediately even before the parent's onNetworksUpdated
   * refetch lands. Cleared when the row's persisted value matches the choice.
   */
  const [methodOptimistic, setMethodOptimistic] = useState<Record<string, string>>({});

  // eUICC inventory modal (per-modem). Visible to super-admin always; to
  // org admins/owners only when the device is org-owned. The Modal
  // component itself enforces nothing — server-side `canWriteDevice`
  // permission gate on /provisioning/euicc/* is the authority.
  // Using useAuth (not useRBACAuth) because RBACAuthProvider doesn't wrap
  // the admin Edge Device Detail tree — the same pattern as useDeviceVisibility.
  const { isSuperAdmin } = useAuth();
  const [euiccModal, setEuiccModal] = useState<{ deviceId: string; modem: CentralEdgeModem; modemImei: string; label: string } | null>(null);

  // Connectivity modes (#252): the table's mode select opens the editor sheet
  // for persistent / hunt (they need an APN etc.); "off" saves directly.
  const [connectivitySheet, setConnectivitySheet] = useState<{ modem: CentralEdgeModem; mode: 'persistent' | 'hunt' } | null>(null);
  const [savingConnectivity, setSavingConnectivity] = useState<Set<string>>(new Set());
  // Modem waiting for confirmation in the DeleteConfirmDialog before it is forgotten
  const [pendingForget, setPendingForget] = useState<CentralEdgeModem | null>(null);
  const handleConnectivityModeSelect = async (modem: CentralEdgeModem, next: string) => {
    const identifier = modem.imei || modem.modemId;
    if (!identifier) return;
    if (next === 'persistent' || next === 'hunt') {
      setConnectivitySheet({ modem, mode: next });
      return;
    }
    if (!modem.connectivityMode) return;
    setSavingConnectivity(prev => new Set(prev).add(identifier));
    try {
      await edgeDeviceService.updateModemSettings(deviceId, identifier, { connectivityMode: null });
      success('Connectivity mode turned off — the modem returns to per-test bring-up');
      onNetworksUpdated?.();
    } catch (err: any) {
      showError(err?.message || 'Failed to turn off connectivity mode');
    } finally {
      setSavingConnectivity(prev => { const s = new Set(prev); s.delete(identifier); return s; });
    }
  };

  const handleSaveEsimMethod = async (modem: CentralEdgeModem, next: string) => {
    const identifier = modem.imei || modem.modemId;
    if (!identifier) return;
    const current = modem.eSimProvisioningMethod ?? '';
    if (next === current) return;
    // Optimistic update — show the new value immediately.
    setMethodOptimistic(prev => ({ ...prev, [identifier]: next }));
    setSavingMethod(prev => new Set(prev).add(identifier));
    try {
      await edgeDeviceService.updateModemSettings(deviceId, identifier, {
        eSimProvisioningMethod: (next === '' ? null : next) as any,
      });
      success(`eSIM method set to ${next || 'Org default'}`);
      onNetworksUpdated?.();
    } catch (err: any) {
      console.error('Failed to save eSIM method:', err);
      showError(err?.message || 'Failed to save eSIM method');
      // Roll back optimistic.
      setMethodOptimistic(prev => {
        const { [identifier]: _, ...rest } = prev;
        return rest;
      });
    } finally {
      setSavingMethod(prev => {
        const s = new Set(prev);
        s.delete(identifier);
        return s;
      });
    }
  };

  const handleSavePriority = async (modem: CentralEdgeModem, raw: string) => {
    const identifier = modem.imei || modem.modemId;
    if (!identifier) return;
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? 0 : Number(trimmed);
    if (!Number.isFinite(parsed)) {
      showError('Priority must be a number');
      return;
    }
    const next = Math.trunc(parsed);
    if (next === (modem.priority ?? 0)) return;
    setSavingPriority(prev => new Set(prev).add(identifier));
    try {
      await edgeDeviceService.updateModemSettings(deviceId, identifier, { priority: next });
      success(`Priority set to ${next}`);
      setPriorityEdits(prev => {
        const { [identifier]: _, ...rest } = prev;
        return rest;
      });
      onNetworksUpdated?.();
    } catch (err: any) {
      console.error('Failed to save priority:', err);
      showError(err?.message || 'Failed to save priority');
    } finally {
      setSavingPriority(prev => {
        const s = new Set(prev);
        s.delete(identifier);
        return s;
      });
    }
  };

  const handleSaveCfunReset = async (modem: CentralEdgeModem, next: boolean) => {
    const identifier = modem.imei || modem.modemId;
    if (!identifier) return;
    setCfunResetOptimistic(prev => ({ ...prev, [identifier]: next }));
    setSavingCfunReset(prev => new Set(prev).add(identifier));
    try {
      await edgeDeviceService.updateModemSettings(deviceId, identifier, { cfunResetOnRelease: next });
      success(next ? 'Full reset (CFUN=1,1) on release enabled' : 'Full reset on release disabled');
      onNetworksUpdated?.();
    } catch (err: any) {
      console.error('Failed to save CFUN reset setting:', err);
      showError(err?.message || 'Failed to save reset setting');
      // Roll back optimistic.
      setCfunResetOptimistic(prev => {
        const { [identifier]: _, ...rest } = prev;
        return rest;
      });
    } finally {
      setSavingCfunReset(prev => {
        const s = new Set(prev);
        s.delete(identifier);
        return s;
      });
    }
  };

  const handleSaveDisabled = async (modem: CentralEdgeModem, nextDisabled: boolean) => {
    const identifier = modem.imei || modem.modemId;
    if (!identifier) return;
    setDisabledOptimistic(prev => ({ ...prev, [identifier]: nextDisabled }));
    setSavingDisabled(prev => new Set(prev).add(identifier));
    try {
      await edgeDeviceService.updateModemSettings(deviceId, identifier, { disabled: nextDisabled });
      success(nextDisabled ? 'Modem disabled — it will not be used for tests' : 'Modem enabled');
      onNetworksUpdated?.();
    } catch (err: any) {
      console.error('Failed to save disabled setting:', err);
      showError(err?.message || 'Failed to save disabled setting');
      // Roll back optimistic.
      setDisabledOptimistic(prev => {
        const { [identifier]: _, ...rest } = prev;
        return rest;
      });
    } finally {
      setSavingDisabled(prev => {
        const s = new Set(prev);
        s.delete(identifier);
        return s;
      });
    }
  };

  // Load modem models from database on mount
  useEffect(() => {
    const loadModemModels = async () => {
      try {
        const models = await modemModelService.getAllModemModels(true); // Only active models
        setModemModels(models);
      } catch (error) {
        console.error('Error loading modem models:', error);
      } finally {
        setModemModelsLoading(false);
      }
    };
    loadModemModels();
  }, []);

  // Find ModemModel by ID
  const findModemModelById = (id: string | null | undefined): ModemModel | null => {
    if (!id) return null;
    return modemModels.find(m => m.id === id) || null;
  };

  // Get effective capabilities (ModemModel defaults + custom overrides)
  const getEffectiveCapabilities = (modem: CentralEdgeModem): ModemCapabilities => {
    const modemModel = findModemModelById(modem.modemModelId);
    return computeEffectiveCapabilities(
      modemModel?.capabilities,
      modem.capabilities
    );
  };

  const handleEditCapabilities = async (modem: CentralEdgeModem) => {
    // Find currently attached ModemModel
    const currentModel = findModemModelById(modem.modemModelId);

    // Check if there are custom capability overrides
    const hasCustom = modem.hasCustomCapabilities || (modem.capabilities && Object.keys(modem.capabilities).length > 0);

    setCapabilitiesModal({
      isOpen: true,
      modem,
      selectedModelId: modem.modemModelId || null,
      selectedModel: currentModel,
      customCapabilities: modem.capabilities,
      showCustomCapabilities: hasCustom || false,
    });
  };

  // Handle ModemModel selection change
  const handleModemModelChange = (modelId: string | null) => {
    const model = modelId ? findModemModelById(modelId) : null;
    setCapabilitiesModal(prev => ({
      ...prev,
      selectedModelId: modelId,
      selectedModel: model,
    }));
  };

  // Handle toggling the custom capabilities switch
  const handleToggleCustomCapabilities = () => {
    setCapabilitiesModal(prev => {
      const turningOn = !prev.showCustomCapabilities;

      // When turning ON, pre-populate with inherited capabilities from the model
      if (turningOn && prev.selectedModel?.capabilities) {
        // Merge model capabilities with any existing custom capabilities
        const inheritedCaps = prev.selectedModel.capabilities;
        const existingCustom = prev.customCapabilities || {};

        // Start with model capabilities, overlay any existing custom values
        const mergedCapabilities = {
          ...inheritedCaps,
          ...existingCustom,
        };

        return {
          ...prev,
          showCustomCapabilities: true,
          customCapabilities: mergedCapabilities,
        };
      }

      return {
        ...prev,
        showCustomCapabilities: turningOn,
      };
    });
  };

  const handleSaveCapabilities = async () => {
    if (!capabilitiesModal.modem) return;

    setSaveError(null);
    setIsSaving(true);

    try {
      // Use the modem's IMEI as the identifier (modemId may not match what the server expects)
      const modemIdentifier = capabilitiesModal.modem.imei || capabilitiesModal.modem.modemId;

      // Save the modem model reference and custom capabilities
      await edgeDeviceService.updateModemSettings(
        deviceId,  // Use the deviceId prop, not from modem object
        modemIdentifier,
        {
          // null (not undefined) so an unlink actually reaches the server —
          // JSON.stringify drops undefined keys, which made "unlink" a silent
          // no-op. Server-side, null clears the link and writes the
          // auto-link tombstone (#191).
          modemModelId: capabilitiesModal.selectedModelId || null,
          modemModelName: capabilitiesModal.selectedModel
            ? `${capabilitiesModal.selectedModel.manufacturer} ${capabilitiesModal.selectedModel.model}`
            : null,
          capabilities: capabilitiesModal.showCustomCapabilities
            ? capabilitiesModal.customCapabilities
            : undefined,
          hasCustomCapabilities: capabilitiesModal.showCustomCapabilities && !!capabilitiesModal.customCapabilities,
        }
      );

      success('Modem settings saved successfully');

      setCapabilitiesModal({
        isOpen: false,
        modem: null,
        selectedModelId: null,
        selectedModel: null,
        customCapabilities: undefined,
        showCustomCapabilities: false,
      });

      // Refresh the device data to show updated settings
      onNetworksUpdated?.();

    } catch (error) {
      console.error('❌ Failed to save modem settings:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save modem settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleScanNetworks = async (modem: CentralEdgeModem) => {
    if (!modem.imei) {
      showError('Cannot scan networks: Modem IMEI not available');
      return;
    }

    setScanningModems(prev => new Set([...prev, modem.imei!]));
    try {
      const result = await edgeDeviceService.scanNetworks(deviceId, modem.imei);
      success(`Network scan completed! Found ${result.operators.length} operators for ${modem.model || modem.modemId}`);
      onNetworksUpdated?.(); // Refresh the modem data
    } catch (error: any) {
      console.error('Failed to scan networks:', error);
      showError(error.message || 'Failed to scan networks');
    } finally {
      setScanningModems(prev => {
        const newSet = new Set(prev);
        if (modem.imei) {
          newSet.delete(modem.imei);
        }
        return newSet;
      });
    }
  };

  const getCapabilitiesBadges = (capabilities: ModemCapabilities | undefined) => {
    if (!capabilities) return null;
    const normalized = normalizeCapabilities(capabilities);
    const badges = [];
    if (getCapabilityValue(normalized, 'supports5G')) badges.push({ label: '5G', color: 'bg-tech-5g-bg text-tech-5g', icon: Wifi });
    if (getCapabilityValue(normalized, 'supports4G')) badges.push({ label: '4G', color: 'bg-status-pending-bg text-status-pending', icon: Radio });
    if (getCapabilityValue(normalized, 'supports3G')) badges.push({ label: '3G', color: 'bg-status-warning-bg text-status-warning', icon: Radio });
    if (getCapabilityValue(normalized, 'supports2G')) badges.push({ label: '2G', color: 'bg-tech-2g-bg text-tech-2g', icon: Radio });
    if (getCapabilityValue(normalized, 'supportsLTEM')) badges.push({ label: 'LTE-M', color: 'bg-tech-4g-bg text-tech-4g', icon: Radio });
    if (getCapabilityValue(normalized, 'supportsNBIoT')) badges.push({ label: 'NB-IoT', color: 'bg-tech-3g-bg text-tech-3g', icon: Radio });
    if (getCapabilityValue(normalized, 'supportsVoLTE')) badges.push({ label: 'VoLTE', color: 'bg-status-success-bg text-status-success', icon: Zap });
    return badges;
  };

  const formatAge = (seconds: number | null | undefined): string | null => {
    if (seconds == null) return null;
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  const handleForgetModem = async (modem: CentralEdgeModem) => {
    const identifier = modem.imei || (modem as any).identifier || modem.id;
    if (!identifier) {
      showError('Cannot forget modem — no identifier');
      return;
    }
    try {
      await edgeDeviceService.forgetModem(deviceId, identifier);
      success('Modem forgotten');
      onModemForgotten?.();
    } catch (err: any) {
      showError(err?.message || 'Failed to forget modem');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-8 h-8 border-4 border-accent-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!modems || modems.length === 0) {
    return (
      <div className="text-center py-12">
        <Smartphone className="w-16 h-16 text-muted-foreground/70 mx-auto mb-4" />
        <p className="text-muted-foreground text-lg">No modems connected to this device</p>
        <p className="text-muted-foreground/70 text-sm mt-2">
          Modems will appear here once they are detected by the edge device
        </p>
      </div>
    );
  }

  // Define columns for table view (using same field mappings as EdgeModemCard)
  const columns: Column<CentralEdgeModem>[] = [
    {
      key: 'model',
      header: 'Model',
      sortable: true,
      width: '18%',
      render: (value, row) => {
        const attachedModel = findModemModelById(row.modemModelId);
        return (
          <div className="space-y-1">
            {/* Hardware-detected model */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">HW:</span>
              <span className="font-medium text-foreground">
                {row.model || row.modemId || 'Unknown'}
              </span>
            </div>
            {/* Attached ModemModel from database */}
            {attachedModel ? (
              <div className="flex items-center gap-1">
                <Link2 className="w-3 h-3 text-accent-blue" />
                <span className="text-xs text-accent-blue font-medium">
                  {attachedModel.manufacturer} {attachedModel.model}
                </span>
                {row.modemModelAutoLinked && (
                  <span
                    className="text-[10px] px-1 rounded bg-muted text-muted-foreground"
                    title="Linked automatically from the modem's reported model string"
                  >
                    auto
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Unlink className="w-3 h-3 text-muted-foreground/50" />
                <span className="text-xs text-muted-foreground/70 italic">
                  No model linked
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'imei',
      header: 'IMEI',
      sortable: true,
      width: '15%',
      render: (value: unknown) => {
        const imei = value as string | undefined;
        return (
          <code className="text-sm text-muted-foreground">{imei || 'N/A'}</code>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: '12%',
      render: (value, row) => {
        const raw = (value as string) || 'unknown';
        // Normalise legacy values so 'connected' reads as 'online' for UX.
        const normalized = raw === 'connected' ? 'online' : raw;
        const ageSeconds = row.age_seconds ?? null;
        const ageLabel = formatAge(ageSeconds);
        const reason = row.reason;
        const identifier = row.imei || row.modemId || '';
        const isDisabled = disabledOptimistic[identifier] ?? (row.disabled === true);
        const noSim = !isSimPresent(row.simInfo, {
          registeredOperator: (row.networkStatus as any)?.operator || row.networkOperator || null,
        });
        return (
          <div className="space-y-1">
            <StatusBadge status={normalized as any} size="sm" />
            {isDisabled && (
              <Badge
                variant="destructive"
                className="text-[10px] px-1.5 py-0 h-5 w-fit"
                title="Admin-disabled — this modem is never used for tests, even while online."
              >
                Disabled
              </Badge>
            )}
            {row.backhaul === true && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-5 w-fit border-accent-blue/50 text-accent-blue flex items-center gap-1"
                title="Device backhaul — this modem is the device's own uplink and is never used for tests. Change it on the Settings tab."
              >
                <RadioTower className="w-3 h-3" />
                Backhaul
              </Badge>
            )}
            {noSim && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-5 w-fit border-status-error/50 text-status-error"
                title="No SIM card detected — this modem can't run network tests."
              >
                No SIM
              </Badge>
            )}
            {ageLabel && (
              <div className="text-xs text-muted-foreground/70" title={`Last seen ${ageLabel} ago`}>
                {ageLabel} ago
              </div>
            )}
            {normalized === 'degraded' && reason && (
              <div className="text-xs text-muted-foreground truncate max-w-[10rem]" title={reason}>
                {reason}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'networkStatus',
      header: 'Network',
      sortable: true,
      width: '15%',
      render: (value, row) => {
        const networkStatus = value as any;
        const network = networkStatus?.network || 
                       networkStatus?.operator || 
                       row.networkOperator || 
                       'N/A';
        const type = networkStatus?.type || 
                    networkStatus?.networkType || 
                    row.networkType;
        
        return (
          <div>
            <div className="text-sm text-foreground">{network}</div>
            {type && (
              <div className="text-xs font-semibold text-muted-foreground">{type}</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'signalQuality',
      header: 'Signal',
      sortable: true,
      width: '20%',
      render: (value, row) => {
        const signal = value as any;
        const percentage = signal?.qualityPercentage || 
                          signal?.quality_percentage || 
                          row.signalQualityPercentage || 
                          0;
        const rssi = signal?.rssiDbm || signal?.rssi_dbm;
        
        return (
          <div className="space-y-1">
            <SignalStrengthBar percentage={percentage} size="sm" />
            <div className="text-xs text-muted-foreground/70">
              {percentage}% {rssi && `(${rssi} dBm)`}
            </div>
          </div>
        );
      }
    },
    {
      key: 'availableNetworks',
      header: 'Networks',
      sortable: false,
      width: '15%',
      render: (value, row) => {
        const networks = row.availableNetworks || [];
        if (networks.length === 0) {
          return (
            <div className="text-xs text-muted-foreground/70">
              No scan data
            </div>
          );
        }

        const uniqueOperators = [...new Set(networks.map(n => n.operator_name))];
        return (
          <div className="space-y-1">
            <div className="flex flex-wrap gap-1">
              {uniqueOperators.slice(0, 2).map((operator, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-status-pending-bg text-status-pending">
                  <Network className="w-3 h-3" />
                  {operator}
                </span>
              ))}
            </div>
            {uniqueOperators.length > 2 && (
              <div className="text-xs text-muted-foreground/70">
                +{uniqueOperators.length - 2} more
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'capabilities',
      header: 'Capabilities',
      sortable: false,
      width: '20%',
      render: (value, row) => {
        // Use effective capabilities (ModemModel defaults + custom overrides)
        const effectiveCaps = getEffectiveCapabilities(row);
        const badges = getCapabilitiesBadges(effectiveCaps);
        const hasAttachedModel = !!row.modemModelId;
        const hasCustomOverrides = row.hasCustomCapabilities;

        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
              {badges && badges.length > 0 ? (
                badges.map((badge, index) => (
                  <span key={index} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${badge.color}`}>
                    <badge.icon className="w-3 h-3" />
                    {badge.label}
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground/70 italic">
                  Not configured
                </span>
              )}
            </div>
            {/* Source indicator */}
            {hasAttachedModel && (
              <div className="text-xs text-muted-foreground/70">
                {hasCustomOverrides ? 'Customized' : 'From model'}
              </div>
            )}
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleScanNetworks(row)}
                disabled={!row.imei || scanningModems.has(row.imei!)}
                className="h-6 px-2 text-xs"
                title="Scan for available networks"
              >
                <Scan className={`w-3 h-3 ${scanningModems.has(row.imei!) ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditCapabilities(row)}
                className="h-6 px-2 text-xs"
                title="Configure model & capabilities"
              >
                <Settings className="w-3 h-3" />
              </Button>
            </div>
          </div>
        );
      }
    },
    {
      key: 'disabled' as any,
      header: 'Enabled',
      sortable: false,
      width: '9%',
      render: (_value, row) => {
        const identifier = row.imei || row.modemId || '';
        const saving = savingDisabled.has(identifier);
        const isDisabled = disabledOptimistic[identifier] ?? (row.disabled === true);
        const isBackhaul = row.backhaul === true;
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={!isDisabled}
              disabled={saving || !identifier || isBackhaul}
              onCheckedChange={(next) => handleSaveDisabled(row, !next)}
              aria-label="Enable modem for tests"
            />
            <span
              className="text-[10px] leading-tight text-muted-foreground max-w-[110px]"
              title={isBackhaul
                ? 'Reserved as the device backhaul — never used for tests. Clear it on the Settings tab to re-enable.'
                : 'When off, this modem is never used for tests — even while it\'s online. Use to park a flaky or reserved modem without unplugging it.'}
            >
              {isBackhaul ? 'Backhaul' : isDisabled ? 'Disabled' : 'Enabled'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      width: '8%',
      render: (value, row) => {
        const identifier = row.imei || row.modemId || '';
        const saving = savingPriority.has(identifier);
        const draft = priorityEdits[identifier];
        const current = (value as number | undefined) ?? 0;
        const shown = draft !== undefined ? draft : String(current);
        return (
          <input
            type="number"
            step={1}
            value={shown}
            disabled={saving || row.backhaul === true}
            onChange={(e) =>
              setPriorityEdits(prev => ({ ...prev, [identifier]: e.target.value }))
            }
            onBlur={(e) => handleSavePriority(row, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                setPriorityEdits(prev => {
                  const { [identifier]: _, ...rest } = prev;
                  return rest;
                });
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-16 px-2 py-1 text-sm font-mono text-right bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
            title="Higher = preferred. Default 0."
          />
        );
      }
    },
    {
      key: 'eSimProvisioningMethod',
      header: 'eSIM',
      sortable: false,
      width: '11%',
      render: (value: unknown, row) => {
        const identifier = row.imei || row.modemId || '';
        // Optimistic value (set immediately on user choice) wins over the
        // stale `row.eSimProvisioningMethod` until the parent refetch lands.
        const current = methodOptimistic[identifier] ?? ((value as string | undefined) ?? '');
        // Effective method = what FlowExecutor will actually do at lock-acquire.
        // Org default is unavailable here (the badge runs on the admin page,
        // not in an org-execution context) — for shared edges any org could
        // execute, so we surface device + per-modem only and label the
        // fallback case explicitly.
        const resolved = resolveProvisioningMethod({
          device: { skipEsimProvisioning: deviceSkipEsimProvisioning },
          modem: { eSimProvisioningMethod: (current || null) as any },
        });
        const shortLabel =
          resolved.method === 'skip' ? 'skip'
          : resolved.method === 'sgp32' ? 'SGP.32'
          : resolved.method === 'lpac_at' ? 'lpac'
          : resolved.method === 'manual' && resolved.source === 'fallback' ? 'org default'
          : 'manual';
        if (!row.imei) {
          return <span className="text-xs text-muted-foreground/70">N/A</span>;
        }
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setEuiccModal({
                deviceId,
                modem: row,
                modemImei: row.imei!,
                label: row.modemModelName || row.model || row.imei!,
              })
            }
            title="Manage eSIM provisioning method & eUICC profile inventory"
            className="h-7 gap-1.5"
          >
            <Cpu className="w-3 h-3" />
            <span className="text-xs">{shortLabel}</span>
          </Button>
        );
      }
    },
    {
      key: 'cfunResetOnRelease',
      header: 'Reset on release',
      sortable: false,
      width: '10%',
      render: (value: unknown, row) => {
        const identifier = row.imei || row.modemId || '';
        const saving = savingCfunReset.has(identifier);
        // Global default ON: only an explicit `false` opts a modem out.
        const persisted = value !== false;
        const checked = cfunResetOptimistic[identifier] ?? persisted;
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={checked}
              disabled={saving || !identifier || !!row.connectivityMode}
              onCheckedChange={(next) => handleSaveCfunReset(row, next)}
              aria-label="Full reset (AT+CFUN=1,1) on release"
            />
            <span
              className="text-[10px] leading-tight text-muted-foreground max-w-[120px]"
              title="ON by default: the modem is fully rebooted (AT+CFUN=1,1) on every allocation release for the cleanest next-run state. Adds ~14–60 s of downtime per release. Turn OFF to use the soft reset (AT+COPS=0 + RAT reset) instead. RAK7391+EG25 is auto-protected — the edge refuses CFUN unless its UART0/SPI0 EDL workaround is live, falling back to soft reset."
            >
              {checked ? 'CFUN=1,1' : 'soft reset'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'connectivityMode' as any,
      header: 'Connectivity',
      sortable: false,
      width: '12%',
      render: (_value, row) => {
        const identifier = row.imei || row.modemId || '';
        const mode = row.connectivityMode || 'off';
        const state = row.connectivityStatus?.state;
        const saving = savingConnectivity.has(identifier);
        // #283 — a running campaign owns this modem's mode. Editing it here would desync
        // the campaign from the modem, so the campaign's own Stop is the only door.
        const ownedByCampaign: string | null = (row as any).connectivityConfig?.campaignRunId ?? null;
        const blocked = !deviceOwnerOrgId || row.backhaul === true || !!ownedByCampaign;
        const stateTone = state === 'attached' || state === 'hunt_attached' || state === 'hunt_terrestrial'
          ? 'default' : state === 'down' || state === 'anchor_mismatch' || state === 'modem_lost' ? 'destructive' : 'secondary';
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Select value={mode} onValueChange={(v) => handleConnectivityModeSelect(row, v)} disabled={saving || !identifier || blocked}>
                <SelectTrigger className="h-7 w-[118px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="persistent">Persistent</SelectItem>
                  <SelectItem value="hunt">PLMN hunt</SelectItem>
                </SelectContent>
              </Select>
              {row.connectivityMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  title="Edit connectivity settings"
                  onClick={() => setConnectivitySheet({ modem: row, mode: row.connectivityMode as 'persistent' | 'hunt' })}
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            {row.connectivityMode && state && (
              <Badge variant={stateTone as any} className="w-fit text-[10px]">
                <Activity className="mr-1 h-3 w-3" />{String(state).replace(/_/g, ' ')}
              </Badge>
            )}
            {ownedByCampaign && (
              <span className="text-[10px] leading-tight text-muted-foreground" title={`Managed by connectivity campaign ${ownedByCampaign}. Stop the campaign in Tests to change this.`}>
                managed by campaign
              </span>
            )}
            {blocked && !ownedByCampaign && !row.connectivityMode && (
              <span className="text-[10px] leading-tight text-muted-foreground" title={!deviceOwnerOrgId ? 'Private (org-owned) edges only' : 'Reserved as backhaul'}>
                {!deviceOwnerOrgId ? 'private edges only' : 'backhaul'}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'port',
      header: 'Port',
      sortable: true,
      width: '8%',
      render: (value: unknown) => {
        const port = value as string | undefined;
        return (
          <div className="text-sm text-muted-foreground">{port || 'N/A'}</div>
        );
      }
    },
    {
      key: 'usb_bus_path',
      header: 'USB',
      sortable: true,
      width: '10%',
      render: (value: unknown, row) => {
        const busPath = value as string | null | undefined;
        const kernelName = row.kernel_name;
        if (!busPath && !kernelName) {
          return <span className="text-sm text-muted-foreground/70">N/A</span>;
        }
        return (
          <div className="space-y-0.5">
            {busPath && (
              <code className="text-xs text-foreground" title="Stable USB slot path (survives reboot)">
                {busPath}
              </code>
            )}
            {kernelName && (
              <div className="text-xs text-muted-foreground/70" title="Kernel tty name (may change across re-enumerations)">
                {kernelName}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'lastSeen',
      header: 'Last Seen',
      sortable: true,
      width: '13%',
      render: (value: unknown, row) => {
        const lastSeen = value as string | number | Date | undefined;
        // Prefer age_seconds from the server — it's derived from the same
        // last_seen_at that drives the status column, so the two rows never
        // disagree. Fall back to whatever `lastSeen` Date we have.
        const ageSeconds = row.age_seconds ?? null;
        if (ageSeconds != null) {
          return (
            <div className="text-sm text-foreground">
              {formatAge(ageSeconds)} ago
            </div>
          );
        }
        if (!lastSeen) return <span className="text-sm text-muted-foreground/70">Never</span>;
        const date = new Date(lastSeen);
        const now = new Date();
        const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
        let timeAgo = 'Just now';
        if (diffMins >= 1 && diffMins < 60) timeAgo = `${diffMins}m ago`;
        else if (diffMins >= 60 && diffMins < 1440) timeAgo = `${Math.floor(diffMins / 60)}h ago`;
        else if (diffMins >= 1440) timeAgo = `${Math.floor(diffMins / 1440)}d ago`;
        return (
          <div>
            <div className="text-sm text-foreground">{timeAgo}</div>
            <div className="text-xs text-muted-foreground/70">{fmt.formatDateTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'forget' as any,
      header: '',
      sortable: false,
      width: '6%',
      render: (_value, row) => {
        // Only offer "forget" for non-live modems. Removing a live one would
        // just race the next heartbeat and reappear.
        if (row.status === 'online' || row.status === 'connected') return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPendingForget(row)}
            title="Forget this modem — removes it from the device's known-modems list"
            className="h-7 w-7 p-0"
          >
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        );
      }
    }
  ];

  const liveCount = modems.filter(m => m.status === 'online' || m.status === 'connected').length;
  const degradedCount = modems.filter(m => m.status === 'degraded').length;
  const offlineCount = modems.filter(m => m.status === 'offline').length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {modems.length} known modem{modems.length !== 1 ? 's' : ''}
          {' — '}
          <span className="text-status-success">{liveCount} online</span>
          {degradedCount > 0 && <>, <span className="text-status-warning">{degradedCount} degraded</span></>}
          {offlineCount > 0 && <>, <span className="text-status-error">{offlineCount} offline</span></>}
        </p>
      </div>

      {/* Modems Table */}
        <DataTable
          columns={columns}
          data={modems}
          keyExtractor={(row) => row.id}
          searchable={true}
          searchPlaceholder="Search by model, IMEI, or port..."
          emptyMessage="No modems found"
        />


      {/* Modem Settings Modal */}
      <Modal
        isOpen={capabilitiesModal.isOpen}
        onClose={() => {
          setCapabilitiesModal({
            isOpen: false,
            modem: null,
            selectedModelId: null,
            selectedModel: null,
            customCapabilities: undefined,
            showCustomCapabilities: false,
          });
          setSaveError(null);
        }}
        title={`Modem Settings - ${capabilitiesModal.modem?.model || capabilitiesModal.modem?.modemId || 'Unknown Modem'}`}
        size="lg"
      >
        <div className="space-y-6">
          {/* Hardware Info Section */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Hardware-Detected Info
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Model:</span>
                <span className="ml-2 font-medium">{capabilitiesModal.modem?.model || 'Unknown'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Manufacturer:</span>
                <span className="ml-2 font-medium">{capabilitiesModal.modem?.manufacturer || 'Unknown'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">IMEI:</span>
                <span className="ml-2 font-mono text-xs">{capabilitiesModal.modem?.imei || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Firmware:</span>
                <span className="ml-2 font-mono text-xs">{capabilitiesModal.modem?.firmwareVersion || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* ModemModel Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Attach to Modem Model
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Link this modem to a predefined model from the database to inherit its capabilities.
            </p>
            <div className="relative">
              <select
                value={capabilitiesModal.selectedModelId || ''}
                onChange={(e) => handleModemModelChange(e.target.value || null)}
                className="w-full h-10 px-3 pr-10 bg-background border border-border rounded-md text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-accent-blue"
                disabled={modemModelsLoading}
              >
                <option value="">-- No model attached --</option>
                {modemModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.manufacturer} {model.model} {model.description ? `- ${model.description}` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* Attached Model Info */}
          {capabilitiesModal.selectedModel && (
            <div className="p-4 bg-accent-blue/10 rounded-lg border border-accent-blue/30">
              <h4 className="text-sm font-medium text-accent-blue mb-3 flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Linked Model: {capabilitiesModal.selectedModel.manufacturer} {capabilitiesModal.selectedModel.model}
              </h4>
              {capabilitiesModal.selectedModel.description && (
                <p className="text-sm text-muted-foreground mb-3">
                  {capabilitiesModal.selectedModel.description}
                </p>
              )}
              <div className="flex flex-wrap gap-1">
                {getCapabilitiesBadges(capabilitiesModal.selectedModel.capabilities)?.map((badge, index) => (
                  <span key={index} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${badge.color}`}>
                    <badge.icon className="w-3 h-3" />
                    {badge.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Custom Capabilities Toggle */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleToggleCustomCapabilities}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  capabilitiesModal.showCustomCapabilities ? 'bg-accent-blue' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    capabilitiesModal.showCustomCapabilities ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <Label
                className="text-sm font-medium cursor-pointer"
                onClick={handleToggleCustomCapabilities}
              >
                Customize capabilities
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {capabilitiesModal.selectedModel
                ? 'Enable to override specific capabilities from the linked model.'
                : 'Enable to manually set capabilities for this modem.'}
            </p>
          </div>

          {/* Custom Capabilities Editor */}
          {capabilitiesModal.showCustomCapabilities && (
            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-medium text-foreground mb-3">
                {capabilitiesModal.selectedModel ? 'Custom Overrides' : 'Capabilities'}
              </h4>
              <ModemCapabilitiesEditor
                capabilities={capabilitiesModal.customCapabilities}
                onChange={(capabilities) => setCapabilitiesModal(prev => ({
                  ...prev,
                  customCapabilities: capabilities
                }))}
              />
            </div>
          )}

          {/* Error Message */}
          {saveError && (
            <div className="p-4 bg-status-error-bg border border-status-error rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-status-error">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-status-error">
                    Failed to save settings
                  </h3>
                  <p className="mt-1 text-sm text-status-error">
                    {saveError}
                  </p>
                </div>
                <button
                  onClick={() => setSaveError(null)}
                  className="flex-shrink-0 text-status-error hover:text-status-error/80"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              variant="secondary"
              onClick={() => {
                setCapabilitiesModal({
                  isOpen: false,
                  modem: null,
                  selectedModelId: null,
                  selectedModel: null,
                  customCapabilities: undefined,
                  showCustomCapabilities: false,
                });
                setSaveError(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSaveCapabilities}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </Modal>

      {/*
        eUICC inventory modal — opens from the single eSIM column button.
        Now also hosts the per-modem provisioning-method selector (rolled in
        from the table column). Shows installed profiles + capacity with
        pin/unpin/manual-evict controls. Server enforces tenancy on every write.
      */}
      <ConnectivityModeSheet
        open={!!connectivitySheet}
        onOpenChange={(o) => { if (!o) setConnectivitySheet(null); }}
        deviceId={deviceId}
        deviceOwnerOrgId={deviceOwnerOrgId}
        modem={connectivitySheet?.modem ?? null}
        initialMode={connectivitySheet?.mode ?? 'persistent'}
        onSaved={() => onNetworksUpdated?.()}
      />

      {euiccModal && (
        <EuiccInventoryModal
          isOpen={true}
          onClose={() => setEuiccModal(null)}
          deviceId={euiccModal.deviceId}
          modemImei={euiccModal.modemImei}
          modemLabel={euiccModal.label}
          isSuperAdmin={!!isSuperAdmin}
          // canManagePins is the same predicate server-side; we mirror it
          // here so the buttons hide gracefully on shared edges for non-
          // super-admins. Server still enforces the real gate.
          canManagePins={!!isSuperAdmin}
          // #247: the device uplink modem gets the guided switch instead of
          // the live enable / delete / install controls.
          isBackhaul={euiccModal.modem.backhaul === true}
          backhaulApn={backhaulApn ?? null}
          eSimProvisioningMethod={
            methodOptimistic[euiccModal.modemImei] ?? euiccModal.modem.eSimProvisioningMethod ?? ''
          }
          deviceSkipEsimProvisioning={deviceSkipEsimProvisioning}
          savingMethod={savingMethod.has(euiccModal.modemImei)}
          onSaveMethod={(next) => handleSaveEsimMethod(euiccModal.modem, next)}
        />
      )}

      <DeleteConfirmDialog
        open={pendingForget !== null}
        onClose={() => setPendingForget(null)}
        title="Forget modem?"
        verb="forget"
        confirmText="Forget"
        itemName={pendingForget ? `${pendingForget.model || 'Modem'} ${pendingForget.imei || (pendingForget as any).identifier || pendingForget.id}` : undefined}
        details="It will disappear from this device until the gateway reports it again."
        onConfirm={async () => {
          if (pendingForget) await handleForgetModem(pendingForget);
        }}
      />
    </div>
  );
};

